/**
 * Cognito glue — Amplify v6 (only @aws-amplify/auth, not the umbrella SDK).
 *
 * `configureAuth()` runs once at app boot and is a no-op when env vars are
 * missing (M5.1 scaffold case). Call it from main.tsx before mounting the
 * router so SignIn / SignUp can call Amplify.Auth methods.
 *
 * `getIdToken()` returns the current JWT, refreshing via Amplify's built-in
 * mechanism if expired. Used by the API fetch wrapper to attach Bearer.
 */

import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
import { env, isConfigured } from './env.ts';

let configured = false;

export function configureAuth(): void {
  if (configured) return;
  if (!isConfigured()) {
    console.warn(
      '[auth] VITE_USER_POOL_ID/VITE_USER_POOL_CLIENT_ID not set — Amplify Auth not configured.',
    );
    return;
  }
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: env.userPoolId,
        userPoolClientId: env.userPoolClientId,
        loginWith: { email: true },
        signUpVerificationMethod: 'code',
        passwordFormat: {
          minLength: 10,
          requireLowercase: true,
          requireUppercase: true,
          requireNumbers: true,
          requireSpecialCharacters: true,
        },
      },
    },
  });
  configured = true;
}

/**
 * Resolve the current user's ID token, or undefined when signed out / unconfigured.
 *
 * Amplify caches the session and refreshes the access token automatically
 * when it's < 5 minutes from expiry. The first call after a cold tab takes
 * ~50ms; subsequent calls are sync.
 */
export async function getIdToken(): Promise<string | undefined> {
  if (!isConfigured()) return undefined;
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch {
    return undefined;
  }
}
